// Semantic diff between two Control Plane documents.
//
// A JSON diff is not a review surface. An operator about to change what every
// installed Gao app renders needs to know four things, and "line 47 changed" is
// none of them: which rows appear, which disappear, which fields moved, and —
// the one people actually get wrong — which rows silently stop rendering on
// some devices without being removed at all.
//
// That last class is why VisibilityChange exists separately from FieldChange.
// Setting `minAppVersion` or flipping `environment` is a one-word edit that
// deletes a row from a population of devices. It looks identical in a JSON diff
// to renaming a category.

import {
  allItems,
  decideItemVisibility,
  HIDDEN_REASON_TEXT,
  inRenderOrder,
  type CatalogItem,
  type ControlPlaneDocument,
  type Lane,
} from "./contract";

export const DIFFABLE_FIELDS = [
  "title",
  "description",
  "category",
  "url",
  "environment",
  "enabled",
  "sortOrder",
  "iconUrl",
  "requiresAuth",
  "minAppVersion",
] as const;

export type DiffableField = (typeof DIFFABLE_FIELDS)[number];

export interface FieldChange {
  field: DiffableField;
  before: unknown;
  after: unknown;
}

export interface AddedRow {
  kind: "added";
  item: CatalogItem;
}
export interface RemovedRow {
  kind: "removed";
  item: CatalogItem;
}
export interface ModifiedRow {
  kind: "modified";
  before: CatalogItem;
  after: CatalogItem;
  changes: FieldChange[];
}
export interface ReorderedRow {
  kind: "reordered";
  item: CatalogItem;
  fromPosition: number;
  toPosition: number;
}

export type RowChange = AddedRow | RemovedRow | ModifiedRow | ReorderedRow;

export interface VisibilityChange {
  itemId: string;
  title: string;
  lane: Lane;
  wasVisible: boolean;
  isVisible: boolean;
  /** Why it is hidden AFTER the change, when it is. */
  reason: string | null;
}

export interface DocumentDiff {
  added: AddedRow[];
  removed: RemovedRow[];
  modified: ModifiedRow[];
  reordered: ReorderedRow[];
  /** Every row whose renderability changes on ANY lane, computed per lane. */
  visibility: VisibilityChange[];
  /** True when nothing at all differs. */
  identical: boolean;
}

function byId(items: CatalogItem[]): Map<string, CatalogItem> {
  return new Map(items.map((i) => [i.id, i]));
}

function fieldChanges(before: CatalogItem, after: CatalogItem): FieldChange[] {
  const out: FieldChange[] = [];
  for (const field of DIFFABLE_FIELDS) {
    if (before[field] !== after[field]) {
      out.push({ field, before: before[field], after: after[field] });
    }
  }
  return out;
}

/**
 * `sortOrder` is reported as a REORDER, not as a field change, whenever the
 * row's rendered position moves — and is suppressed entirely when it does not.
 * Rewriting every row's sortOrder to close a gap is a routine editor operation
 * that changes ten numbers and moves nothing; reporting ten field changes for
 * it would bury the one row that actually moved.
 */
function positions(items: CatalogItem[]): Map<string, number> {
  const ordered = inRenderOrder(items);
  return new Map(ordered.map((i, idx) => [i.id, idx]));
}

export function diffDocuments(
  before: ControlPlaneDocument,
  after: ControlPlaneDocument,
  lanes: readonly Lane[] = ["production", "test", "dev"],
): DocumentDiff {
  const beforeItems = allItems(before);
  const afterItems = allItems(after);
  const beforeMap = byId(beforeItems);
  const afterMap = byId(afterItems);
  const beforePos = positions(beforeItems);
  const afterPos = positions(afterItems);

  const added: AddedRow[] = [];
  const removed: RemovedRow[] = [];
  const modified: ModifiedRow[] = [];
  const reordered: ReorderedRow[] = [];

  for (const item of inRenderOrder(afterItems)) {
    const prior = beforeMap.get(item.id);
    if (!prior) {
      added.push({ kind: "added", item });
      continue;
    }
    const changes = fieldChanges(prior, item).filter(
      // sortOrder is handled by the reorder pass below.
      (c) => c.field !== "sortOrder",
    );
    if (changes.length > 0) {
      modified.push({ kind: "modified", before: prior, after: item, changes });
    }
    const from = beforePos.get(item.id);
    const to = afterPos.get(item.id);
    if (from !== undefined && to !== undefined && from !== to) {
      reordered.push({ kind: "reordered", item, fromPosition: from, toPosition: to });
    }
  }

  for (const item of inRenderOrder(beforeItems)) {
    if (!afterMap.has(item.id)) removed.push({ kind: "removed", item });
  }

  // ── Visibility, per lane ──────────────────────────────────────────────────
  const visibility: VisibilityChange[] = [];
  for (const lane of lanes) {
    // A version-gated row is reported against a client that DECLARES a
    // version, because "hidden from a client that declares nothing" is true of
    // every gated row and would drown the signal. The console shows the
    // declared-version case; the editor's preview covers specific versions.
    const probeVersion = "999999.0.0";
    const ids = new Set([...beforeMap.keys(), ...afterMap.keys()]);
    for (const id of ids) {
      const b = beforeMap.get(id);
      const a = afterMap.get(id);
      const was = b ? decideItemVisibility(b, lane, probeVersion).visible : false;
      const isDecision = a ? decideItemVisibility(a, lane, probeVersion) : null;
      const is = isDecision ? isDecision.visible : false;
      if (was === is) continue;
      visibility.push({
        itemId: id,
        title: (a ?? b)!.title,
        lane,
        wasVisible: was,
        isVisible: is,
        reason:
          isDecision && !isDecision.visible
            ? HIDDEN_REASON_TEXT[isDecision.reason]
            : a
              ? null
              : "row removed",
      });
    }
  }

  return {
    added,
    removed,
    modified,
    reordered,
    visibility,
    identical:
      added.length === 0 &&
      removed.length === 0 &&
      modified.length === 0 &&
      reordered.length === 0,
  };
}

/** `null` renders as an em dash, booleans as on/off — a diff that shows
 *  `false` and `null` as bare words reads as if a field were deleted. */
export function renderValue(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (v === true) return "on";
  if (v === false) return "off";
  return String(v);
}

/** One-line summary for a list row or a confirmation dialog. */
export function summarize(d: DocumentDiff): string {
  if (d.identical) return "no changes";
  const parts: string[] = [];
  if (d.added.length) parts.push(`+${d.added.length} added`);
  if (d.removed.length) parts.push(`−${d.removed.length} removed`);
  if (d.modified.length) parts.push(`~${d.modified.length} modified`);
  if (d.reordered.length) parts.push(`↕${d.reordered.length} reordered`);
  return parts.join(" · ");
}
